import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';

import '@aws-amplify/ui-react/styles.css';
import { Amplify } from 'aws-amplify';
import awsconfig from './aws-exports';

// State for error handling
import { Alert } from '@mui/material';

// Material UI Components
import {
  Typography,
  Button,
  IconButton,
  Box
} from '@mui/material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';

// Custom Hooks
import { useFileOperations } from './hooks/useFileOperations';
import { useJobParameters } from './hooks/useJobParameters';
import { callApi } from './utils/apiHelper';
// import NavigationBar from './components/NavigationBar';
import ResultPage from './components/ResultPage';
import HomePage from './components/HomePage';
import UserProfile from './components/UserProfile';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

Amplify.configure(awsconfig);

export default function App() {
  const [mode, setMode] = useState(() => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const dynamicTheme = createTheme({
    palette: {
      mode,
      primary: {
        main: '#1976d2',
      },
      secondary: {
        main: '#dc004e',
      },
    },
  });

  return (
    <ThemeProvider theme={dynamicTheme}>
      <CssBaseline />
      <Authenticator.Provider>
        <AppContent toggleTheme={toggleTheme} mode={mode} />
      </Authenticator.Provider>
    </ThemeProvider>
  );
}

function AppContent({ toggleTheme, mode }) {
  const { user, signOut, authStatus } = useAuthenticator((context) => [
    context.user,
    context.signOut,
    context.authStatus
  ]);
  const [error, setError] = useState(null);

  const fileOperations = useFileOperations(
    user,
    (params) => callApi({ user, ...params }),
    setError
  );

  const jobParameters = useJobParameters();

  if (authStatus === 'configuring') return <div>Loading...</div>;
  if (authStatus !== 'authenticated') return <Authenticator />;



  return (
    <Box sx={{
      width: '80%',           // Changed to 80% of viewport width
      minWidth: '1200px',      // Added minimum width for responsiveness
      maxWidth: '3200px',     // Added maximum width to prevent extreme stretching
      margin: '0 auto',       // Center the content
      padding: '0 1em'        // Optional: add some padding
    }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1em'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5em'
        }}>
          <img
            src={`${process.env.PUBLIC_URL}/edansoul.png`}
            alt="Logo"
            style={{ width: '28px', height: '28px', verticalAlign: 'middle' }}
          />
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              lineHeight: '1',
              margin: '0'
            }}
          >
            Welcome to the Edansoul TS Service!
          </Typography>
        </Box>
        <IconButton onClick={toggleTheme} color="inherit">
          {mode === 'light' ? <Brightness4Icon /> : <Brightness7Icon />}
        </IconButton>
      </Box>

      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1em'
      }}>
        <UserProfile mode={mode} />
        <Button
          variant="contained"
          color="primary"
          onClick={signOut}
        >
          Sign out
        </Button>
      </Box>

      <BrowserRouter>
        {/* <NavigationBar mode={mode} /> */}
        <Routes>
          <Route path="/" element={<HomePage
            mode={mode}
            fileOperations={fileOperations}
            jobParameters={jobParameters}
            user={user}
            callApi={callApi}
          />} />
          <Route path="/resultpage" element={<ResultPage
            mode={mode}
            user={user}
          />} />
        </Routes>
      </BrowserRouter>

      {error && (
        <Alert severity="error" sx={{ marginTop: '1em' }}>
          {error.message || JSON.stringify(error)}
        </Alert>
      )}
    </Box>
  );
}
